import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Create axios instance
const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('rejuven_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('rejuven_token');
      localStorage.removeItem('rejuven_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),  // Now requires invite_token
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
};

// Users API
export const usersAPI = {
  completeOnboarding: (data) => api.post('/users/onboarding', data),
  updateProfile: (data) => api.put('/users/profile', data),
  getDashboardStats: () => api.get('/users/dashboard-stats'),
  updateLanguage: (language) => api.put('/users/language', { language }),
};

// Chat API
export const chatAPI = {
  getHistory: () => api.get('/chat/history'),
  sendMessage: (message) => api.post('/chat/send', { message }),
  clearHistory: () => api.delete('/chat/history'),
  getProactiveMessage: () => api.get('/chat/proactive-message'),
};

// Check-ins API
export const checkinsAPI = {
  getQuestions: () => api.get('/checkins/questions'),
  submit: (data) => api.post('/checkins/submit', data),
  getHistory: (limit = 30) => api.get(`/checkins/history?limit=${limit}`),
  getToday: () => api.get('/checkins/today'),
};

// Reports API
export const reportsAPI = {
  getAll: () => api.get('/reports/'),
  getById: (id) => api.get(`/reports/${id}`),
};

// Notifications API
export const notificationsAPI = {
  getAll: () => api.get('/notifications/'),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/read-all'),
};

// Admin API
export const adminAPI = {
  getUsers: () => api.get('/admin/users'),
  getUser: (id) => api.get(`/admin/users/${id}`),
  createUser: (data) => api.post('/admin/users', data),
  deleteUser: (id) => api.delete(`/admin/users/${id}`),
  completeUserOnboarding: (userId, data) => api.put(`/admin/users/${userId}/onboarding`, data),
  uploadReport: (userId, reportType, title, reportDate, file) => {
    const formData = new FormData();
    formData.append('file', file);
    let url = `/admin/users/${userId}/reports?report_type=${reportType}&title=${encodeURIComponent(title)}`;
    if (reportDate) {
      url += `&report_date=${encodeURIComponent(reportDate)}`;
    }
    return api.post(url, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  updateReport: (reportId, data) => api.put(`/admin/reports/${reportId}`, data),
  deleteReport: (reportId) => api.delete(`/admin/reports/${reportId}`),
  askAboutReport: (reportId, question) => api.post(`/admin/reports/${reportId}/ask?question=${encodeURIComponent(question)}`),
  getDocuments: () => api.get('/admin/documents'),
  uploadDocument: (title, category, description, file, folderId = null) => {
    const formData = new FormData();
    formData.append('file', file);
    let url = `/admin/documents?title=${encodeURIComponent(title)}&category=${category}&description=${encodeURIComponent(description || '')}`;
    if (folderId) {
      url += `&folder_id=${folderId}`;
    }
    return api.post(url, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteDocument: (id) => api.delete(`/admin/documents/${id}`),
  moveDocument: (id, folderId) => api.put(`/admin/documents/${id}/move?folder_id=${folderId || ''}`),
  reextractDocument: (id) => api.post(`/admin/documents/${id}/reextract`),
  getStats: () => api.get('/admin/stats'),
  // Health Goals Management
  getHealthGoals: () => api.get('/admin/health-goals'),
  createHealthGoal: (data) => api.post('/admin/health-goals', data),
  updateHealthGoal: (id, data) => api.put(`/admin/health-goals/${id}`, data),
  deleteHealthGoal: (id) => api.delete(`/admin/health-goals/${id}`),
  // Health Issues Management
  getHealthIssues: () => api.get('/admin/health-issues'),
  createHealthIssue: (data) => api.post('/admin/health-issues', data),
  updateHealthIssue: (id, data) => api.put(`/admin/health-issues/${id}`, data),
  deleteHealthIssue: (id) => api.delete(`/admin/health-issues/${id}`),
  // Genotype Management
  getGenotypes: () => api.get('/admin/genotypes'),
  createGenotype: (data) => api.post('/admin/genotypes', data),
  updateGenotype: (id, data) => api.put(`/admin/genotypes/${id}`, data),
  deleteGenotype: (id) => api.delete(`/admin/genotypes/${id}`),
  uploadGenotypeImage: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/admin/genotypes/${id}/upload-image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  assignUserGenotype: (userId, genotypeId) => api.put(`/admin/users/${userId}/genotype?genotype_id=${genotypeId || ''}`),
  // User Reminders Management
  getUserReminders: (userId) => api.get(`/admin/users/${userId}/reminders`),
  createUserReminder: (userId, data) => api.post(`/admin/users/${userId}/reminders`, data),
  deleteReminder: (id) => api.delete(`/admin/reminders/${id}`),
  // Document Folders Management
  getFolders: () => api.get('/admin/folders'),
  createFolder: (data) => api.post('/admin/folders', data),
  updateFolder: (id, data) => api.put(`/admin/folders/${id}`, data),
  deleteFolder: (id) => api.delete(`/admin/folders/${id}`),
  getFolderDocuments: (id) => api.get(`/admin/folders/${id}/documents`),
  // Access Requests Management
  getAccessRequests: (status) => api.get(`/invitations/admin/requests${status ? `?status=${status}` : ''}`),
  approveAccessRequest: (id) => api.post(`/invitations/admin/requests/${id}/approve`),
  rejectAccessRequest: (id, reason) => api.post(`/invitations/admin/requests/${id}/reject${reason ? `?reason=${encodeURIComponent(reason)}` : ''}`),
  deleteAccessRequest: (id) => api.delete(`/invitations/admin/requests/${id}`),
};

// Invitations API (public)
export const invitationsAPI = {
  requestAccess: (data) => api.post('/invitations/request-access', data),
  validateToken: (token) => api.get(`/invitations/validate/${token}`),
};

// Public Health Goals (for onboarding)
export const healthGoalsAPI = {
  getAll: () => api.get('/health-goals'),
};

// Public Health Issues (for onboarding)
export const healthIssuesAPI = {
  getAll: () => api.get('/health-issues'),
};

// Genotypes API (for users)
export const genotypesAPI = {
  getMyGenotype: () => api.get('/genotypes/me'),
  getById: (id) => api.get(`/genotypes/${id}`),
  getRecommendations: () => api.get('/genotypes/me/recommendations'),
};

// Reminders API (for users)
export const remindersAPI = {
  getAll: () => api.get('/reminders/'),
  getToday: () => api.get('/reminders/today'),
  create: (data) => api.post('/reminders/', data),
  update: (id, data) => api.put(`/reminders/${id}`, data),
  complete: (id) => api.post(`/reminders/${id}/complete`),
  delete: (id) => api.delete(`/reminders/${id}`),
  createFromAI: (data) => api.post('/reminders/from-ai', data),
};

// Wellness API (RAG-powered recommendations with caching)
export const wellnessAPI = {
  getDiet: (forceRefresh = false) => api.get(`/wellness/diet${forceRefresh ? '?force_refresh=true' : ''}`),
  getSupplements: (forceRefresh = false) => api.get(`/wellness/supplements${forceRefresh ? '?force_refresh=true' : ''}`),
  getLifestyle: (forceRefresh = false) => api.get(`/wellness/lifestyle${forceRefresh ? '?force_refresh=true' : ''}`),
  searchFood: (query) => api.get(`/wellness/diet/search?query=${encodeURIComponent(query)}`),
};

// Testimonials API
export const testimonialsAPI = {
  // Public
  getPublic: () => api.get('/testimonials/public'),
  // Admin
  getAll: () => api.get('/testimonials/'),
  getById: (id) => api.get(`/testimonials/${id}`),
  create: (data) => api.post('/testimonials/', data),
  update: (id, data) => api.put(`/testimonials/${id}`, data),
  delete: (id) => api.delete(`/testimonials/${id}`),
  seed: () => api.post('/testimonials/seed'),
};

// Setup admin (one-time)
export const setupAdmin = () => api.post('/setup-admin');

// Contact form API (public)
export const contactAPI = {
  submit: (data) => api.post('/contact', data),
};

// Pillar lead-capture API (public)
export const leadsAPI = {
  submit: (data) => api.post('/leads', data),
};

export default api;
